import React, { useEffect, useMemo, useRef, useState } from 'react';

import { abrirEsteiraParalela } from '../../../api/esteirasParalelasApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';
// O casco (`status-modal-overlay`, `LightboxStatus`) vive no CSS do StatusModal. Importado
// explicitamente: contar com o bundle carregar o arquivo da tela vizinha é o acoplamento
// invisível do BUG-V74.
import '../status/StatusModal.css';

/** Mesmo teto do backend (`NOTA_CURTA_MAX_LENGTH`): nota curta, não relato. */
const MOTIVO_MAX_LENGTH = 200;

interface AbrirEsteiraParalelaModalProps {
  carId: string;
  plate: string;
  /** A etapa em que o veículo está — não pode ser aberta em paralelo (já é a principal). */
  esteiraPrincipalId?: string;
  esteiraPrincipalNome?: string;
  /** Ids das esteiras já abertas em paralelo, para não oferecer o que dará 409. */
  jaAbertasIds?: string[];
  /** Ids das esteiras liberadas pela administração para este veículo (ADR 0015). */
  esteirasLiberadasIds?: string[];
  /** Liberação TOTAL: todas as esteiras de trabalho estão liberadas. */
  liberacaoTotal?: boolean;
  onCancel: () => void;
  onAberta: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * "Abrir esteira em paralelo" (at-99, ADR 0050) — só o Chefe de Oficina.
 *
 * O veículo continua na etapa em que está; o que a ação faz é permitir que outro setor trabalhe
 * nele ao mesmo tempo, apontando as horas na esteira do próprio serviço. É o que substitui o
 * vai-e-volta de esteira que a oficina fazia para "emprestar" o carro a outro ofício — medido em
 * produção (10/09/2026): 12 de 41 veículos ativos, e 34% das passagens por esteira de trabalho
 * durando menos de uma hora.
 *
 * ⚠️ **Esteira sem liberação administrativa aparece DESABILITADA com o motivo**, e não escondida:
 * escondê-la faria o Chefe procurar uma opção que existe e não entender por que não está lá. Sem
 * a liberação (ADR 0015), nenhuma sessão poderia começar ali e a paralela nasceria inútil.
 *
 * ⚠️ Reusa o casco do `StatusModal` (`status-modal-overlay` / `LightboxStatus`), como o
 * `MotivoEsteiraModal` da at-89 — nunca CSS próprio de modal. Usa o segundo nível da pilha
 * (`modal-overlay--empilhado-2`) porque pode abrir sobre o `ControleParalelasModal`, que já é
 * empilhado sobre o modal de ações; fecha por clique fora e por `Escape`.
 */
const AbrirEsteiraParalelaModal: React.FC<AbrirEsteiraParalelaModalProps> = ({
  carId,
  plate,
  esteiraPrincipalId,
  esteiraPrincipalNome,
  jaAbertasIds = [],
  esteirasLiberadasIds = [],
  liberacaoTotal = false,
  onCancel,
  onAberta,
  sidebarRef,
}) => {
  const { esteirasAtivas } = useEsteiras();
  const modalRef = useRef<HTMLDivElement>(null);
  const [esteiraId, setEsteiraId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);

  /**
   * As candidatas: esteira de TRABALHO, ativa, que aceita colaborador, que não é a principal e
   * que ainda não está aberta. Cada uma carrega se está liberada — a liberação decide se o item
   * fica clicável, não se aparece.
   */
  const candidatas = useMemo(
    () =>
      (esteirasAtivas as any[])
        .filter(
          (e) =>
            e.sistema === false &&
            e.permiteColaborador !== false &&
            e.id !== esteiraPrincipalId &&
            !jaAbertasIds.includes(e.id),
        )
        .map((e) => ({
          id: e.id as string,
          nome: e.nome as string,
          liberada: liberacaoTotal || esteirasLiberadasIds.includes(e.id),
        })),
    [esteirasAtivas, esteiraPrincipalId, jaAbertasIds, esteirasLiberadasIds, liberacaoTotal],
  );

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (modalRef.current?.contains(alvo)) return;
      if (sidebarRef?.current?.contains(alvo)) return;
      onCancel();
    };
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onCancel();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', escape);
    };
  }, [onCancel, sidebarRef]);

  const handleAbrir = async () => {
    if (!esteiraId) return;
    setSalvando(true);
    try {
      const criada = await abrirEsteiraParalela(carId, {
        esteiraId,
        ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
      });
      notify.success(
        `${criada.nome} foi aberta em paralelo. O veículo continua em ${
          esteiraPrincipalNome ?? 'sua etapa atual'
        } e agora aceita atividade nas duas esteiras.`,
      );
      onAberta();
      onCancel();
    } catch (error) {
      // Aqui o `errorFrom` é o certo: o backend explica O QUE impediu (falta de liberação,
      // etapa de fluxo livre, esteira que não aceita colaborador) e cada motivo pede uma ação
      // diferente do Chefe. Compare com o erro de ENCERRAMENTO, onde o que importa é a
      // consequência e o texto é fixo (regra da at-65).
      notify.errorFrom(
        error,
        'Não foi possível abrir a esteira em paralelo. Nenhuma alteração foi feita no veículo.',
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="status-modal-overlay modal-overlay--empilhado-2">
      <div className="status-modal-content" ref={modalRef}>
        <div className="LightboxStatus">
          <div className="LightboxStatusForm">
            <div className="EditarCarro">Abrir esteira em paralelo — {plate}</div>

            <p style={{ fontSize: 13, color: 'var(--tema-texto-2, #5E5E5E)', lineHeight: 1.45, margin: '0 0 4px' }}>
              O veículo continua em <strong>{esteiraPrincipalNome ?? 'sua etapa atual'}</strong>: a
              etapa não muda, o prazo dela não reinicia e o quadro não troca de coluna. O que muda é
              que outro setor passa a poder trabalhar nele ao mesmo tempo, apontando as horas na
              esteira do próprio serviço.
            </p>

            <div className="CampoStatus">
              <div className="CampoTitulo">Qual esteira vai trabalhar em paralelo?</div>
              <div className="DropDownFormulRioStatus">
                <div className="InputSelect">
                  <select
                    className="Input"
                    value={esteiraId}
                    onChange={(evento) => setEsteiraId(evento.target.value)}
                  >
                    <option value="">Selecione a esteira</option>
                    {candidatas.map((candidata) => (
                      <option
                        key={candidata.id}
                        value={candidata.id}
                        disabled={!candidata.liberada}
                      >
                        {candidata.nome}
                        {candidata.liberada ? '' : ' — sem liberação da administração'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {candidatas.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--tema-texto-2, #5E5E5E)' }}>
                Não há esteira de trabalho disponível para abrir em paralelo neste veículo. As
                etapas de fluxo livre e as que não recebem colaborador não entram, e as já abertas
                em paralelo também não.
              </p>
            )}

            <div className="CampoStatus">
              <div className="CampoTitulo">Motivo (opcional)</div>
              <div className="DropDownFormulRioStatus">
                <div className="InputSelect">
                  <input
                    className="Input"
                    type="text"
                    maxLength={MOTIVO_MAX_LENGTH}
                    placeholder="Ex: mecânico precisa adiantar a revisão"
                    value={motivo}
                    onChange={(evento) => setMotivo(evento.target.value)}
                  />
                </div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: 'var(--tema-texto-2, #5E5E5E)', lineHeight: 1.4, margin: 0 }}>
              O motivo é opcional e serve de registro: com o tempo, mostra por que e com que
              frequência a oficina precisa trabalhar em duas esteiras ao mesmo tempo.
            </p>

            <div className="Frame1000003998Status">
              <div className="CtaBigEditar cancel">
                <button
                  type="button"
                  className="CustomFileCancel"
                  onClick={onCancel}
                  disabled={salvando}
                >
                  Cancelar
                </button>
              </div>
              <button
                type="button"
                className="CtaBigEditar save"
                onClick={() => void handleAbrir()}
                disabled={!esteiraId || salvando}
              >
                {salvando ? 'Abrindo...' : 'Abrir em paralelo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbrirEsteiraParalelaModal;
